import { useRef, useState, useEffect } from 'react';
import { useToast } from 'context/providers/ToastProvider';

export const WHATSAPP_FILE_LIMITS = {
  image: { maxBytes: 5 * 1024 * 1024, maxMb: 5, label: 'La imagen' },
  video: { maxBytes: 16 * 1024 * 1024, maxMb: 16, label: 'El video' },
  audio: { maxBytes: 16 * 1024 * 1024, maxMb: 16, label: 'El audio' },
  document: { maxBytes: 100 * 1024 * 1024, maxMb: 100, label: 'El documento' }
};

export const validateWhatsAppFileSize = file => {
  if (!file) return { isValid: true };
  const type = (file.type || '').toLowerCase();
  let category = 'document';
  if (type.startsWith('image/')) category = 'image';
  else if (type.startsWith('video/')) category = 'video';
  else if (type.startsWith('audio/')) category = 'audio';

  const limit = WHATSAPP_FILE_LIMITS[category];
  if (file.size > limit.maxBytes) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    return {
      isValid: false,
      error: `${limit.label} (${sizeMb} MB) supera el límite máximo de ${limit.maxMb} MB permitido por WhatsApp.`
    };
  }
  return { isValid: true };
};

export const useSendMessageLogic = ({
  inputText,
  setInputText,
  onSendMessage,
  selectedFile,
  setSelectedFile,
  filePreview,
  setFilePreview,
  isSending,
  commands = [],
  quickCommands = [],
  onSelectCommand,
  clientOpenTickets = [],
  inputRef: externalInputRef
}) => {
  const { showErrorToast } = useToast();
  const fileInputRef = useRef(null);
  const internalInputRef = useRef(null);
  const textInputRef = externalInputRef || internalInputRef;
  const suggestionsRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const plusMenuRef = useRef(null);

  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [showTicketNoteModal, setShowTicketNoteModal] = useState(false);
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false);
  const [isImageEditorOpen, setIsImageEditorOpen] = useState(false);

  const [filteredCommands, setFilteredCommands] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Cerrar emoji picker y menú de adjuntos al hacer clic afuera
  useEffect(() => {
    const handleClickOutside = e => {
      if (showEmojiPicker && emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
      if (showPlusMenu && plusMenuRef.current && !plusMenuRef.current.contains(e.target) && !e.target.closest('.btn-plus-attach')) {
        setShowPlusMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker, showPlusMenu]);

  // Normalizar los comandos recibidos para autocompletar con '/'
  const normalizedCommands = (commands || []).map(cmd => ({
    command: cmd.vComando || cmd.label || '',
    title: cmd.vTitulo || cmd.title || cmd.label || '',
    text: cmd.vTexto || cmd.text || ''
  }));

  // Auto-ajuste de altura dinámico para el textarea (Shift + Enter multilínea)
  const adjustHeight = () => {
    const el = textInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const scrollH = el.scrollHeight;
    const newH = Math.min(Math.max(scrollH, 44), 160);
    el.style.height = `${newH}px`;
  };

  useEffect(() => {
    adjustHeight();
  }, [inputText]);

  // Detectar trigger '/' cuando el usuario escribe
  useEffect(() => {
    if (!inputText) {
      setShowSuggestions(false);
      return;
    }

    const match = inputText.match(/(?:^|\s)\/([a-zA-Z0-9_-]*)$/);

    if (match !== null) {
      const query = match[1].toLowerCase();
      const matches = normalizedCommands.filter(cmd => {
        const cmdClean = cmd.command.replace(/^\//, '').toLowerCase();
        const titleClean = cmd.title.toLowerCase();
        return cmdClean.includes(query) || titleClean.includes(query);
      });

      if (matches.length > 0) {
        setFilteredCommands(matches);
        setSelectedIndex(0);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  }, [inputText, commands]);

  const applyCommand = cmd => {
    const updated = inputText.replace(/(?:^|\s)\/([a-zA-Z0-9_-]*)$/, match => {
      const startsWithSpace = match.startsWith(' ');
      return (startsWithSpace ? ' ' : '') + cmd.text;
    });

    setInputText(updated);
    setShowSuggestions(false);
    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus();
        const len = updated.length;
        textInputRef.current.setSelectionRange?.(len, len);
      }
    }, 20);
  };

  // Helper para formato WhatsApp (*negrita*, _cursiva_, ~tachado~, ```código```)
  const applyFormatWrapper = (prefix, suffix = prefix, defaultPlaceholder = 'texto') => {
    const el = textInputRef.current;
    if (!el) return;

    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const selected = inputText.substring(start, end);
    const content = selected || defaultPlaceholder;
    const replacement = `${prefix}${content}${suffix}`;
    const newText = inputText.substring(0, start) + replacement + inputText.substring(end);
    setInputText(newText);
    setTimeout(() => {
      el.focus();
      if (selected) {
        el.setSelectionRange(start, start + replacement.length);
      } else {
        el.setSelectionRange(start + prefix.length, start + prefix.length + defaultPlaceholder.length);
      }
    }, 20);
  };

  // Helper para insertar emoji
  const insertEmoji = emoji => {
    const el = textInputRef.current;
    if (!el) {
      setInputText(prev => prev + emoji);
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const newText = inputText.substring(0, start) + emoji + inputText.substring(end);
    setInputText(newText);
    setTimeout(() => {
      el.focus();
      const nextPos = start + emoji.length;
      el.setSelectionRange(nextPos, nextPos);
    }, 20);
  };

  const handleKeyDown = e => {
    // Atajos de formato: Ctrl+B, Ctrl+I, Ctrl+Shift+X
    if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
      e.preventDefault();
      applyFormatWrapper('*', '*', 'negrita');
      return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key === 'i' || e.key === 'I')) {
      e.preventDefault();
      applyFormatWrapper('_', '_', 'cursiva');
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'x' || e.key === 'X')) {
      e.preventDefault();
      applyFormatWrapper('~', '~', 'tachado');
      return;
    }

    if (showSuggestions && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        applyCommand(filteredCommands[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
        return;
      }
    }

    // Al presionar Enter: Shift+Enter crea nueva línea, Enter solo envía
    if (e.key === 'Enter') {
      if (e.shiftKey) return;
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Manejo de adjuntos desde el menú '+'
  const handleSelectPlusAction = actionType => {
    setShowPlusMenu(false);

    if (actionType === 'note') {
      setShowTicketNoteModal(true);
      return;
    }

    if (!fileInputRef.current) return;

    if (actionType === 'media') {
      fileInputRef.current.accept = 'image/*,video/*';
    } else if (actionType === 'audio') {
      fileInputRef.current.accept = 'audio/*,.mp3,.ogg,.wav,.m4a,.aac';
    } else if (actionType === 'document') {
      fileInputRef.current.accept = '.pdf,.doc,.docx,.xls,.xlsx,.txt,application/pdf';
    }
    fileInputRef.current.click();
  };

  const handleFileChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateWhatsAppFileSize(file);
    if (!validation.isValid) {
      if (showErrorToast) {
        showErrorToast(validation.error);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        setFilePreview({ type: 'image', url: reader.result, name: file.name, size: file.size });
        setIsImageEditorOpen(true);
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('video/')) {
      setFilePreview({ type: 'video', url: '', name: file.name, size: file.size });
    } else if (file.type.startsWith('audio/')) {
      setFilePreview({ type: 'audio', url: '', name: file.name, size: file.size });
    } else {
      setFilePreview({ type: 'document', url: '', name: file.name, size: file.size });
    }
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 20);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setIsImageEditorOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    textInputRef.current?.focus();
  };

  const handleSaveEditedImage = (newFile, newPreview) => {
    setSelectedFile(newFile);
    setFilePreview(newPreview);
    setIsImageEditorOpen(false);
  };

  const handleSendEditedImage = (newFile, newPreview, captionText) => {
    setSelectedFile(newFile);
    setFilePreview(newPreview);
    if (typeof captionText === 'string') {
      setInputText(captionText);
    }
    setIsImageEditorOpen(false);
    onSendMessage(
      null,
      typeof captionText === 'string' ? captionText : undefined,
      newFile,
      newPreview
    );
  };

  const handleDiscardImage = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setIsImageEditorOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    textInputRef.current?.focus();
  };

  const handleTriggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = 'image/*';
      fileInputRef.current.click();
    }
  };

  const handlePaste = e => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          const validation = validateWhatsAppFileSize(file);
          if (!validation.isValid) {
            if (showErrorToast) {
              showErrorToast(validation.error);
            }
            return;
          }
          setSelectedFile(file);
          const url = URL.createObjectURL(file);
          setFilePreview({ url, type: 'image', name: file.name || 'imagen_pegada.png', size: file.size });
          setIsImageEditorOpen(true);
          break;
        }
      }
    }
  };

  const handleSubmit = e => {
    if (e) e.preventDefault();
    if (showSuggestions) {
      setShowSuggestions(false);
    }
    setShowEmojiPicker(false);
    setShowPlusMenu(false);
    onSendMessage(e);
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 10);
  };

  // Atajos rápidos dinámicos
  const hasOpenTickets = clientOpenTickets && clientOpenTickets.length > 0;
  const firstTicket = hasOpenTickets ? clientOpenTickets[0] : null;
  const firstTicketId = firstTicket?.idTicket || firstTicket?.id;
  const firstSubject = firstTicket?.vAsunto ? ` respecto a "${firstTicket.vAsunto}"` : '';

  const mergePromptMessage = clientOpenTickets.length === 1
    ? `Hola, notamos que ya cuentas con el ticket #${firstTicketId} abierto en atención${firstSubject}. ¿Deseas que combinemos esta conversación con tu ticket existente para darte seguimiento conjunto en un solo reporte?`
    : `Hola, identificamos que cuentas con tickets abiertos en atención. ¿Deseas que unifiquemos esta conversación con tu ticket previo para darte seguimiento continuo en un solo reporte?`;

  const mergePillLabel = '/combinar';
  const mergePillTitle = firstTicketId
    ? `Preguntar al cliente si desea combinar con Ticket #${firstTicketId}`
    : 'Preguntar al cliente si desea combinar tickets';

  const handleShortcutClick = text => {
    if (onSelectCommand) {
      onSelectCommand(text, false);
    } else {
      setInputText(prev => (prev ? prev + ' ' + text : text));
      setTimeout(() => textInputRef.current?.focus(), 20);
    }
  };

  const displayQuickCommands = (quickCommands && quickCommands.length > 0 ? quickCommands : commands) || [];
  const canSend = !isSending && (Boolean(inputText.trim()) || Boolean(selectedFile));

  return {
    textInputRef,
    fileInputRef,
    suggestionsRef,
    emojiPickerRef,
    plusMenuRef,

    // Menú '+' y modal de notas
    showPlusMenu,
    setShowPlusMenu,
    handleSelectPlusAction,
    showTicketNoteModal,
    setShowTicketNoteModal,

    // Modal de atajos
    isShortcutsModalOpen,
    setIsShortcutsModalOpen,

    // Slash commands
    showSuggestions,
    filteredCommands,
    selectedIndex,
    setSelectedIndex,
    applyCommand,

    // Formato y emojis
    applyFormatWrapper,
    showEmojiPicker,
    setShowEmojiPicker,
    insertEmoji,

    // Archivos
    handleFileChange,
    handleRemoveFile,
    handlePaste,
    isImageEditorOpen,
    setIsImageEditorOpen,
    handleSaveEditedImage,
    handleSendEditedImage,
    handleDiscardImage,
    handleTriggerFileSelect,

    // Enviar y teclas
    handleKeyDown,
    handleSubmit,
    canSend,

    // Atajos
    hasOpenTickets,
    mergePillTitle,
    mergePillLabel,
    mergePromptMessage,
    displayQuickCommands,
    handleShortcutClick
  };
};
